/**
 * Layer 1: Aeries CSV/Excel Adapter
 *
 * Parses the long-format Aeries query export (one row per student per class)
 * and pivots it into the normalized per-student schedule format used by
 * the shared schedule import pipeline.
 *
 * Supports both .xlsx and .csv files.
 */

import * as XLSX from 'xlsx'
import {
  contactHasValue,
  looksLikeGuardian,
  rankContacts,
  sparseContact
} from '@/utils/studentPrivateUtils.js'
import { FIRST_NAME_ALIAS_HEADERS, firstNameWithAlias } from '@/utils/aeriesName.js'
import { nameFromEmailLocal, staffNameFromAeries } from '@/utils/userDisplayUtils.js'

/**
 * Parse an Aeries export file (.xlsx or .csv) into raw rows.
 * @param {File} file - The uploaded file
 * @returns {Promise<{ headers: string[], rows: object[] }>}
 */
export async function parseAeriesFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]

        // Duplicate PrintQuery titles (STU.FN and CON.FN are both
        // "First Name") come back suffixed: "First Name", "First Name_1".
        // headerMatches() strips that suffix so both copies still match.
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' })

        if (!jsonData.length) {
          return reject(new Error('No data rows found in the file.'))
        }

        const headers = Object.keys(jsonData[0])
        resolve({ headers, rows: jsonData })
      } catch (err) {
        reject(new Error('Failed to parse file: ' + err.message))
      }
    }

    reader.onerror = () => reject(new Error('Failed to read file.'))
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Auto-detect column mapping from headers.
 * Returns a mapping of logical field -> actual header name.
 * @param {string[]} headers
 * @returns {{ mapping: object, unmapped: string[], confidence: string }}
 */
/**
 * Normalize an Aeries header for matching.
 * PrintQueryToExcel uses friendly names ("Parent/guardian", "ParentEmail")
 * instead of field codes (STU.PG, STU.PEM).
 */
function headerForms(value) {
  const lower = String(value || '')
    .toLowerCase()
    .trim()
  const spaced = lower
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
  const compact = spaced.replace(/\s+/g, '')
  return { lower, spaced, compact }
}

/**
 * PrintQuery repeats "First Name" / "Last Name" for STU and CON, and Aeries
 * dedupes the titles with a bare digit ("Name2", "First Name1"); SheetJS
 * uses "First Name_1". Strip the trailing counter (with or without a space)
 * so later copies still match the "first name" candidate.
 */
function headerMatches(have, want) {
  if (have.lower === want.lower || have.spaced === want.spaced) return true
  if (want.compact.length > 2 && have.compact === want.compact) return true
  const baseSpaced = have.spaced.replace(/\s*\d+$/, '').trim()
  const baseCompact = baseSpaced.replace(/\s+/g, '')
  if (baseSpaced && baseSpaced === want.spaced) return true
  return want.compact.length > 2 && baseCompact === want.compact
}

/**
 * Pick a header by walking candidates in order (first candidate that exists
 * wins). Prefer this over "first header that matches any candidate" so
 * STU.CID is chosen over STU.ID when both are in the file — STU.ID is the
 * local permanent ID, not the SSID.
 *
 * Compact matching is skipped for tokens of 2 chars or less (`id`, `pd`,
 * `pg`) so they cannot collide with longer friendly names.
 */
function pickHeader(headers, headerMeta, candidates, occurrence = 0) {
  for (const candidate of candidates) {
    const want = headerForms(candidate)
    let found = 0
    for (let i = 0; i < headerMeta.length; i++) {
      if (!headerMatches(headerMeta[i], want)) continue
      if (found === occurrence) return headers[i]
      found++
    }
  }
  return null
}

export function detectColumnMapping(headers) {
  const headerMeta = headers.map((h) => headerForms(h))
  const mapping = {}
  const unmapped = []

  const patterns = {
    teacherName: ['teacher name', 'tch.te', 'teacher'],
    // SSID first. Never list stu.id here — that is the local student number.
    stateStudentId: ['state student id', 'state id', 'ssid', 'stu.cid', 'cid'],
    studentId: ['student id', 'stu.id', 'id'],
    lastName: ['last name', 'stu.ln', 'lastname', 'last'],
    firstName: ['first name', 'stu.fn', 'firstname', 'first'],
    // STU.FNA. Aeries shows this in parentheses next to the first name.
    // Optional: a file without it still imports.
    firstNameAlias: FIRST_NAME_ALIAS_HEADERS,
    period: ['period', 'mst.pd', 'sms.pd', 'pd'],
    courseName: ['course title', 'course name', 'crs.cn', 'coursename'],
    courseCode: ['course id', 'course code', 'crs.co', 'courseid'],
    teacherEmail: ['email', 'tch.em', 'teacher email', 'teacheremail'],
    sectionNumber: ['section#', 'section', 'sec.se', 'sss.se'],
    userField: ['user5', 'stu.u5', 'u5'],
    grade: ['grade', 'grade level', 'stu.gr', 'gr'],
    // LOC.SC is the state school id — the 7-digit school part of the CDS.
    // It usually exports with the header "School". STU.SC is the district's
    // own site code (1–999) and is only a backup: codes under 4 digits are
    // ignored by the school check, so it degrades to "no file code".
    // Matching is exact after normalization, so bare "school" does not
    // swallow "School Name".
    schoolCode: [
      'state school id',
      'state school number',
      'loc.sc',
      'school code',
      'cds code',
      'school cds code',
      'school cds',
      'school',
      'stu.sc',
      'cds',
      'org sourced id',
      'orgsourcedid'
    ]
  }

  // Wide format (one row per student): STU.PG / TL / PEM, or Contact 1 / Contact 2.
  // Long format (one row per contact): CON.NM / CON.OR / CON.PC / CON.RL / phones.
  const contactPatterns = {
    contact1Name: [
      'contact 1 name',
      'contact1 name',
      'primary contact name',
      'parent name',
      'parent/guardian name',
      'parent/guardian',
      'parent guardian',
      'stu.pg',
      'pg',
      'con.nm'
    ],
    contact1Relationship: [
      'contact 1 relationship',
      'contact1 relationship',
      'primary contact relationship',
      'con.rl',
      'con.rl?'
    ],
    contact1Phone: [
      'contact 1 phone',
      'contact1 phone',
      'primary contact phone',
      'primary phone',
      'parent phone',
      'stu.tl',
      'tl',
      'home phone',
      'fathers work',
      "father's work",
      'father work',
      'stu.fw',
      'fw',
      'con.cp',
      'con.te'
    ],
    contact1Email: [
      'contact 1 email',
      'contact1 email',
      'primary contact email',
      'parent email',
      'parentemail',
      'stu.pem',
      'pem',
      'email address',
      'con.em'
    ],
    contact2Name: ['contact 2 name', 'contact2 name', 'secondary contact name'],
    contact2Relationship: [
      'contact 2 relationship',
      'contact2 relationship',
      'secondary contact relationship'
    ],
    contact2Phone: [
      'contact 2 phone',
      'contact2 phone',
      'secondary contact phone',
      'mothers work',
      "mother's work",
      'mother work',
      'stu.mw',
      'mw'
    ],
    contact2Email: ['contact 2 email', 'contact2 email', 'secondary contact email'],
    // CON table — one contact per row. "Name" is CON.NM's PrintQuery title.
    // Do not list student First/Last Name here; the second copy is assigned below.
    contactName: ['mailing name', 'contact name', 'con.nm', 'name', 'nm'],
    contactFirstName: ['contact first name', 'con.fn', 'cnc first name'],
    contactLastName: ['contact last name', 'con.ln', 'cnc last name'],
    // "Description_CON_RL" etc. are the PrintQuery titles for CON.RL? /
    // CON.CD? / CON.LW? (the ? expands the code to its description).
    contactRelationship: [
      'relationship',
      'contact relationship',
      'description con rl',
      'con.rl',
      'con.rl?'
    ],
    contactRecordType: ['record type', 'description con cd', 'con.cd', 'con.cd?'],
    contactCellPhone: ['mobile phone', 'cell phone', 'con.cp'],
    contactHomePhone: ['home telephone', 'home phone', 'telephone', 'con.tl'],
    contactWorkPhone: ['work phone', 'work telephone', 'con.wp'],
    contactEmail: [
      'email address',
      'e mail address',
      'e-mail address',
      'contact email',
      'con.em',
      'email',
      'e mail',
      'e-mail'
    ],
    contactOrder: ['contact order', 'con.or', 'con.sq', 'sequence', 'order'],
    contactPrimary: ['primary contact', 'con.pc', 'con.pc?'],
    contactLivesWith: [
      'lives with',
      'lives with student',
      'description con lw',
      'con.lw',
      'con.lw?'
    ]
  }

  for (const [field, candidates] of Object.entries(patterns)) {
    const header = pickHeader(headers, headerMeta, candidates)
    if (header) mapping[field] = header
    else if (field !== 'schoolCode' && field !== 'firstNameAlias') unmapped.push(field)
  }

  for (const [field, candidates] of Object.entries(contactPatterns)) {
    const header = pickHeader(headers, headerMeta, candidates)
    if (header) mapping[field] = header
  }

  // PrintQuery repeats First/Last Name for CON.FN / CON.LN after the student
  // columns. Prefer an unused later copy so we do not store the student name
  // as the contact name.
  if (!mapping.contactFirstName) {
    const extraFirst = pickHeader(headers, headerMeta, ['first name', 'firstname', 'first'], 1)
    if (extraFirst) mapping.contactFirstName = extraFirst
  }
  if (!mapping.contactLastName) {
    const extraLast = pickHeader(headers, headerMeta, ['last name', 'lastname', 'last'], 1)
    if (extraLast) mapping.contactLastName = extraLast
  }

  const hasStudentKey = mapping.stateStudentId || mapping.studentId
  const requiredFields = ['period', 'teacherEmail']
  const missingRequired = requiredFields.filter((f) => !mapping[f])
  if (!hasStudentKey) missingRequired.push('stateStudentId or studentId')

  // Students are matched on the state ID (SSID). Without that column the pivot
  // falls back to the local student number, which matches nothing — every
  // student then lands in "not in app" with no hint as to why. The import can
  // still run (a district may simply name its SSID column "Student ID"), but
  // the fallback is named here so the admin can check before spending a pass.
  const studentKeyIsLocalId = !mapping.stateStudentId && !!mapping.studentId

  let confidence = 'high'
  if (missingRequired.length > 0) confidence = 'low'
  else if (studentKeyIsLocalId || unmapped.length > 2) confidence = 'medium'

  return {
    mapping,
    unmapped,
    missingRequired,
    confidence,
    studentKeyIsLocalId,
    studentKeyColumn: mapping.stateStudentId || mapping.studentId || '',
    isLongContactFormat: isLongContactFormat(mapping)
  }
}

/**
 * Read a cell as trimmed text.
 *
 * `|| ''` cannot be used here: an .xlsx parses numeric cells as numbers, so a
 * zero-period class or a grade of 0 would coalesce to '' and the row would look
 * empty. Only null/undefined mean "no value".
 */
export function cellText(value) {
  return value == null ? '' : String(value).trim()
}

/** STU.FN plus a distinct STU.FNA, as Aeries displays it: Hex (Benny). */
export function studentFirstName(row, columnMapping = {}) {
  const first = cellText(row?.[columnMapping.firstName])
  const alias = columnMapping.firstNameAlias ? cellText(row?.[columnMapping.firstNameAlias]) : ''
  return firstNameWithAlias(first, alias)
}

export const PROGRAM_EXCLUDE = '__EXCLUDE__'

/**
 * Suggested schedule query. One row per student per class.
 * The second line is an example filter: the field and codes vary by school
 * and district. The upload step shows that line in a different color.
 * Do not join CON.
 */
export const AERIES_SCHEDULE_QUERY = `LIST STU SEC MST CRS TCH STU.ID STU.CID STU.LN STU.FN STU.FNA STU.GR STU.U5 SEC.SE MST.PD CRS.CN CRS.CO TCH.TE TCH.EM
IF ( STU.U5 = 'R' OR STU.U5 = 'S' OR STU.U5 = 'F' OR STU.U5 = 'O' OR STU.U5 = 'X' OR STU.U5 = 4 ) AND ( MST.SM = 'F' OR MST.SM = 'Y' )`

/** First line is the column list; the rest is the example filter. */
export function splitExampleQuery(query) {
  const [columns, ...filterLines] = String(query || '').split('\n')
  return { columns, filter: filterLines.join('\n') }
}

/** STU + CON query that maps to the app's guardian contact list. */
export const AERIES_CONTACTS_QUERY = `LIST STU CON STU.CID STU.LN STU.FN STU.FNA STU.GR STU.U5 CON.OR CON.PC CON.NM CON.FN CON.LN CON.RL? CON.CD? CON.CP CON.TL CON.WP CON.EM CON.LW?
IF ( STU.U5 = 'R' OR STU.U5 = 'S' OR STU.U5 = 'F' OR STU.U5 = 'O' OR STU.U5 = 'X' OR STU.U5 = 4 )`

/** STU + FOF + FAP — 504 accommodations. Table order matters; do not join CON.
 * LOC.SC is the state school id (7-digit school part of the CDS; the column
 * usually exports as "School"). STU.SC is the district's own site code —
 * backup only. LOG is optional. LOG.CD is the changed table (FAP or FOF),
 * not LOG.ID. (( DTS )) is Aeries' date-time stamp. Leave LOG.CD out of the
 * IF so a student with no log row is still exported. */
export const AERIES_504_QUERY = `LIST STU LOC FOF FAP LOG STU.CID LOC.SC STU.SC STU.LN STU.FN STU.FNA STU.GR STU.U5 FOF.RD FOF.DE FAP.AD FAP.AD? FAP.CD FAP.CD? FAP.AC FAP.FN FAP.FN? LOG.CD LOG.USR (( DTS ))
IF STU.U5 = 4 AND FOF.SD # NULL AND FOF.ED = NULL`

/** Same columns. Replace the 504 code with the one this school stores in STU.U5. */
export const AERIES_504_QUERY_SCHOOL = `LIST STU LOC FOF FAP LOG STU.CID LOC.SC STU.SC STU.LN STU.FN STU.FNA STU.GR STU.U5 FOF.RD FOF.DE FAP.AD FAP.AD? FAP.CD FAP.CD? FAP.AC FAP.FN FAP.FN? LOG.CD LOG.USR (( DTS ))
IF ( STU.U5 = 4 OR STU.U5 = 'your 504 code' ) AND FOF.SD # NULL AND FOF.ED = NULL`

/** Use when the LOG join errors, drops students, or (( DTS )) is rejected. */
export const AERIES_504_QUERY_NO_LOG = `LIST STU LOC FOF FAP STU.CID LOC.SC STU.SC STU.LN STU.FN STU.FNA STU.GR STU.U5 FOF.RD FOF.DE FAP.AD FAP.AD? FAP.CD FAP.CD? FAP.AC FAP.FN FAP.FN?
IF STU.U5 = 4 AND FOF.SD # NULL AND FOF.ED = NULL`

/** Use when (( DTS )) fails but LOG.DT is accepted. The date has no time. */
export const AERIES_504_QUERY_LOG_DATE = `LIST STU LOC FOF FAP LOG STU.CID LOC.SC STU.SC STU.LN STU.FN STU.FNA STU.GR STU.U5 FOF.RD FOF.DE FAP.AD FAP.AD? FAP.CD FAP.CD? FAP.AC FAP.FN FAP.FN? LOG.CD LOG.USR LOG.DT
IF STU.U5 = 4 AND FOF.SD # NULL AND FOF.ED = NULL`

function headerLooksLike(header, candidates) {
  if (!header) return false
  const have = headerForms(header)
  return candidates.some((candidate) => headerMatches(have, headerForms(candidate)))
}

function hasConContactSignals(columnMapping = {}) {
  return Boolean(
    columnMapping.contactName ||
    columnMapping.contactFirstName ||
    columnMapping.contactLastName ||
    columnMapping.contactOrder ||
    columnMapping.contactPrimary ||
    columnMapping.contactCellPhone ||
    columnMapping.contactWorkPhone ||
    columnMapping.contactLivesWith ||
    columnMapping.contactRecordType
  )
}

/**
 * True when the export is one row per Aeries CON record (not STU.PG wide).
 * Wide Contact 1 / Contact 2 columns win so a schedule file stays wide.
 */
export function isLongContactFormat(columnMapping = {}) {
  if (
    columnMapping.contact2Name ||
    columnMapping.contact2Email ||
    columnMapping.contact2Relationship
  ) {
    return false
  }
  const wideName = columnMapping.contact1Name || ''
  const stuParentGuardian = headerLooksLike(wideName, [
    'parent/guardian',
    'parent guardian',
    'parent/guardian name',
    'parent name',
    'stu.pg',
    'pg'
  ])
  if (stuParentGuardian && !hasConContactSignals(columnMapping)) return false
  return (
    hasConContactSignals(columnMapping) || headerLooksLike(wideName, ['mailing name', 'con.nm'])
  )
}

/**
 * Pull the optional parent/guardian contact columns out of a single export row.
 * Contact values repeat on every class row for a student, so any row works.
 * Returns an ordered array (index 0 = primary) or null when the export has no
 * contact columns / no values.
 * @param {object} row
 * @param {object} columnMapping
 * @returns {Array<{ order: number, name: string, relationship: string, phone: string, email: string }>|null}
 */
export function extractContactsFromRow(row, columnMapping) {
  const val = (field) => (columnMapping[field] ? cellText(row[columnMapping[field]]) : '')

  const contacts = []
  for (const n of [1, 2]) {
    const contact = {
      order: n,
      name: val(`contact${n}Name`),
      relationship: val(`contact${n}Relationship`),
      phone: val(`contact${n}Phone`),
      email: val(`contact${n}Email`).toLowerCase()
    }
    if (contact.name || contact.phone || contact.email) contacts.push(contact)
  }
  return contacts.length ? contacts : null
}

/**
 * One CON-table row → one sparse contact. Cell / home / work are stored
 * separately; `phone` is the preferred compat value (cell, then home, then work).
 */
export function extractContactFromLongRow(row, columnMapping) {
  const val = (field) => (columnMapping[field] ? cellText(row[columnMapping[field]]) : '')
  const firstName = val('contactFirstName')
  const lastName = val('contactLastName')
  const name =
    val('contactName') || val('contact1Name') || [firstName, lastName].filter(Boolean).join(' ')
  const relationship = val('contactRelationship') || val('contact1Relationship')
  const recordType = val('contactRecordType')
  // CON.LW? prints the code description, not Y/N — any non-"no" value is set.
  const livesWithText = val('contactLivesWith')
  const livesWith = livesWithText === '' ? '' : !/^(n|no|false|0)$/i.test(livesWithText)
  const contact = sparseContact({
    name,
    firstName,
    lastName,
    relationship,
    recordType,
    cellPhone: val('contactCellPhone'),
    homePhone: val('contactHomePhone'),
    workPhone: val('contactWorkPhone'),
    phone: val('contact1Phone'),
    email: val('contactEmail') || val('contact1Email'),
    order: val('contactOrder'),
    primary: val('contactPrimary'),
    livesWith
  })
  if (!contactHasValue(contact)) return null
  return {
    ...contact,
    guardian: looksLikeGuardian(relationship, recordType)
  }
}

function rankLongContacts(a, b) {
  const base = rankContacts(a, b)
  if (base !== 0) return base
  return a.rowIndex - b.rowIndex
}

function finalizeLongContacts(list) {
  return [...list]
    .sort(rankLongContacts)
    .map((c) => {
      const { rowIndex, guardian, ...rest } = c
      return sparseContact(rest)
    })
    .filter(contactHasValue)
}

/** True when the mapping found at least one guardian contact column. */
export function mappingHasContactColumns(columnMapping = {}) {
  return Boolean(
    columnMapping.contact1Name ||
    columnMapping.contact1Phone ||
    columnMapping.contact1Email ||
    columnMapping.contact2Name ||
    columnMapping.contact2Phone ||
    columnMapping.contact2Email ||
    columnMapping.contactName ||
    columnMapping.contactFirstName ||
    columnMapping.contactLastName ||
    columnMapping.contactCellPhone ||
    columnMapping.contactHomePhone ||
    columnMapping.contactWorkPhone ||
    columnMapping.contactEmail ||
    columnMapping.contactPrimary ||
    columnMapping.contactLivesWith
  )
}

/**
 * Pivot an Aeries contacts export into { ssid: { firstName, lastName, contacts } }.
 *
 * Long format (STU+CON): one row per contact; ranked by primary flag,
 * lives-with, guardian relationship, then CON.OR. All rows are kept.
 * Wide format (STU.PG or Contact 1/2): one row per student. Duplicate SSIDs
 * keep the first non-empty contacts; later rows backfill if the first was blank.
 */
export function pivotToContactRoster(rows, columnMapping) {
  const students = {}
  const noIdStudents = new Map()
  const warnings = []
  const longFormat = isLongContactFormat(columnMapping)

  const studentIdColumn = columnMapping.stateStudentId || columnMapping.studentId

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const studentId = cellText(row[studentIdColumn])
    const firstName = studentFirstName(row, columnMapping)
    const lastName = cellText(row[columnMapping.lastName])

    if (!studentId) {
      const who = `${firstName} ${lastName}`.trim()
      const key = who.toLowerCase() || `row-${i + 2}`
      const seen = noIdStudents.get(key)
      if (seen) seen.rows++
      else noIdStudents.set(key, { name: who, firstRow: i + 2, rows: 1 })
      continue
    }

    if (longFormat) {
      const contact = extractContactFromLongRow(row, columnMapping)
      if (!students[studentId]) {
        students[studentId] = { firstName, lastName, _raw: [] }
      } else {
        if (!students[studentId].firstName && firstName) students[studentId].firstName = firstName
        if (!students[studentId].lastName && lastName) students[studentId].lastName = lastName
      }
      if (contact) students[studentId]._raw.push({ ...contact, rowIndex: i })
      continue
    }

    const contacts = extractContactsFromRow(row, columnMapping)
    if (!students[studentId]) {
      students[studentId] = { firstName, lastName, contacts: contacts || null }
    } else if (!students[studentId].contacts && contacts) {
      students[studentId].contacts = contacts
    }
  }

  if (longFormat) {
    for (const student of Object.values(students)) {
      const finalized = finalizeLongContacts(student._raw || [])
      student.contacts = finalized.length ? finalized : null
      delete student._raw
    }
  }

  for (const entry of noIdStudents.values()) {
    warnings.push({
      row: entry.firstRow,
      message:
        `${entry.name || 'A student'} has no ${studentIdColumn || 'student ID'} — ` +
        `contact row(s) skipped. Add the SSID in Aeries (STU.CID) and re-export.`
    })
  }

  const withContacts = Object.values(students).filter((s) => s.contacts?.length).length

  return {
    students,
    warnings,
    stats: {
      totalRows: rows.length,
      uniqueStudents: Object.keys(students).length,
      withContacts,
      withoutContacts: Object.keys(students).length - withContacts,
      skippedNoId: noIdStudents.size,
      warningCount: warnings.length
    }
  }
}

/**
 * Resolve the program/plan value from a raw userField value using the mapping config.
 * @param {string} rawValue - The raw value from the Aeries export (e.g., '4', 'x', 's')
 * @param {object|null} programMapping - The programFieldMapping config from app_settings
 * @returns {string} Resolved plan value ('IEP', '504', PROGRAM_EXCLUDE, or raw value if no mapping)
 */
export function resolveProgram(rawValue, programMapping) {
  if (!rawValue) return ''
  const normalized = String(rawValue).trim().toLowerCase()
  if (!normalized) return ''

  if (!programMapping?.enabled || !programMapping?.mappings) {
    return rawValue
  }

  for (const [key, plan] of Object.entries(programMapping.mappings)) {
    if (key.toLowerCase() === normalized) return plan
  }

  return programMapping.unmappedDefault || rawValue
}

/**
 * Which id this schedule row is keyed by.
 *
 * SSID when the state-ID cell is filled. When that column exists but the cell
 * is blank, STU.ID is the key and the row is tagged `aeriesId` so it is not
 * written into `app.studentData.ssid`. A file with no SSID column at all still
 * treats its id column as the SSID — a district may name that column "Student ID".
 */
function scheduleStudentKey(row, columnMapping) {
  const stateId = columnMapping.stateStudentId ? cellText(row[columnMapping.stateStudentId]) : ''
  const aeriesId = columnMapping.studentId ? cellText(row[columnMapping.studentId]) : ''
  if (stateId) {
    return {
      key: stateId,
      matchOn: 'ssid',
      stateStudentId: stateId,
      aeriesStudentId: aeriesId
    }
  }
  if (columnMapping.stateStudentId && aeriesId) {
    return {
      key: aeriesId,
      matchOn: 'aeriesId',
      stateStudentId: '',
      aeriesStudentId: aeriesId
    }
  }
  if (aeriesId) {
    return {
      key: aeriesId,
      matchOn: 'ssid',
      stateStudentId: aeriesId,
      aeriesStudentId: ''
    }
  }
  return null
}

/**
 * Pivot long-format Aeries data into the normalized intermediate format.
 *
 * @param {object[]} rows - Raw parsed rows
 * @param {object} columnMapping - Mapping of logical field -> actual header name
 * @param {Set<string>} skipPeriods - Period numbers to skip (e.g., new Set(["0", "8"]))
 * @param {object|null} programMapping - Optional programFieldMapping config from app_settings
 * @returns {object} Normalized schedule data
 */
export function pivotToStudentSchedules(
  rows,
  columnMapping,
  skipPeriods = new Set(),
  programMapping = null
) {
  const students = {}
  const excludedStudents = {}
  const warnings = []
  const noIdStudents = new Map()
  const allTeacherEmails = new Set()
  const teacherNamesByEmail = {}
  let skippedRows = 0

  const studentIdColumn = columnMapping.stateStudentId || columnMapping.studentId

  // Students are keyed by state ID (SSID) downstream. Say so up front when the
  // file has no state-ID column, rather than letting it surface as a roster
  // full of unmatched students.
  if (!columnMapping.stateStudentId && columnMapping.studentId) {
    warnings.push({
      row: 0,
      message:
        `No state student ID (SSID) column found — students are keyed on ` +
        `"${columnMapping.studentId}" instead. If that column holds the local Aeries ` +
        `student number rather than the SSID, none of them will match the app. ` +
        `Re-export with STU.CID to fix it.`
    })
  }

  // Resolve the program source column from the config (case-insensitive header match).
  // Falls back to the auto-detected userField column if no sourceColumn is configured.
  let programColumn = null
  if (programMapping?.enabled && programMapping?.sourceColumn && rows.length) {
    const target = programMapping.sourceColumn.trim().toLowerCase()
    const actualKey = Object.keys(rows[0]).find((k) => k.trim().toLowerCase() === target)
    programColumn = actualKey || null
    if (!actualKey) {
      warnings.push({
        row: 0,
        message: `Program mapping column "${programMapping.sourceColumn}" not found in the file. Falling back to auto-detected field.`
      })
    }
  }
  if (!programColumn && columnMapping.userField) {
    programColumn = columnMapping.userField
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]

    const keyed = scheduleStudentKey(row, columnMapping)
    const studentId = keyed?.key || ''
    const period = cellText(row[columnMapping.period])
    const teacherEmail = cellText(row[columnMapping.teacherEmail])

    if (!keyed) {
      // One warning per student, not per class row: a student with no state ID
      // produces one of these for every period, which buries the fact that a
      // whole schedule is being dropped and never says whose.
      const who =
        `${studentFirstName(row, columnMapping)} ${cellText(row[columnMapping.lastName])}`.trim()
      const key = who.toLowerCase() || `row-${i + 2}`
      const seen = noIdStudents.get(key)
      if (seen) seen.rows++
      else noIdStudents.set(key, { name: who, firstRow: i + 2, rows: 1 })
      continue
    }
    if (!period) {
      warnings.push({ row: i + 2, message: `Missing period for student ${studentId}` })
      continue
    }

    if (skipPeriods.has(period)) {
      skippedRows++
      continue
    }

    const firstName = studentFirstName(row, columnMapping)
    const lastName = cellText(row[columnMapping.lastName])
    const courseName = columnMapping.courseName ? cellText(row[columnMapping.courseName]) : ''
    const courseCode = columnMapping.courseCode ? cellText(row[columnMapping.courseCode]) : ''
    const sectionNumber = columnMapping.sectionNumber
      ? cellText(row[columnMapping.sectionNumber])
      : ''

    const grade = columnMapping.grade ? cellText(row[columnMapping.grade]) : ''
    const programRaw = programColumn ? cellText(row[programColumn]) : ''
    const plan = resolveProgram(programRaw, programMapping)

    if (plan === PROGRAM_EXCLUDE) {
      if (!excludedStudents[studentId]) {
        excludedStudents[studentId] = { firstName, lastName, grade, programRaw }
      }
      continue
    }

    // Key collision: one student's Aeries ID equals another student's SSID.
    // Same matchOn is the same student (the key IS that id); for an SSID key,
    // the Aeries ID column is supplemental — backfill it when the first row's
    // cell was blank, and warn (keep the first) if two values disagree.
    const existingStudent = students[studentId]
    if (existingStudent && existingStudent.matchOn !== keyed.matchOn) {
      warnings.push({
        row: i + 2,
        message:
          `ID ${studentId} is already used by ${existingStudent.firstName} ${existingStudent.lastName}. ` +
          `Skipped ${firstName} ${lastName}.`
      })
      continue
    }
    if (existingStudent && keyed.aeriesStudentId) {
      if (!existingStudent.aeriesStudentId) {
        existingStudent.aeriesStudentId = keyed.aeriesStudentId
      } else if (existingStudent.aeriesStudentId !== keyed.aeriesStudentId) {
        warnings.push({
          row: i + 2,
          message:
            `Two Aeries student IDs for ${firstName} ${lastName} (${studentId}): ` +
            `${existingStudent.aeriesStudentId} and ${keyed.aeriesStudentId}. Keeping the first.`
        })
      }
    }

    if (!students[studentId]) {
      students[studentId] = {
        firstName,
        lastName,
        grade,
        plan,
        programRaw,
        matchOn: keyed.matchOn,
        stateStudentId: keyed.stateStudentId,
        aeriesStudentId: keyed.aeriesStudentId,
        contacts: extractContactsFromRow(row, columnMapping),
        periods: {}
      }
    } else if (!students[studentId].contacts) {
      // Contact columns repeat per class row; backfill if the first row was blank.
      students[studentId].contacts = extractContactsFromRow(row, columnMapping)
    }

    if (students[studentId].periods[period]) {
      warnings.push({
        row: i + 2,
        message:
          `Duplicate period ${period} for student ${firstName} ${lastName} (${studentId}). ` +
          `Existing: ${students[studentId].periods[period].courseName}, New: ${courseName}. Using latest.`
      })
    }

    students[studentId].periods[period] = {
      teacherEmail: teacherEmail.toLowerCase(),
      courseName,
      courseCode,
      sectionNumber
    }

    if (teacherEmail) {
      const emailKey = teacherEmail.toLowerCase()
      allTeacherEmails.add(emailKey)
      const rawTeacherName = columnMapping.teacherName
        ? cellText(row[columnMapping.teacherName])
        : ''
      const name = staffNameFromAeries(rawTeacherName, teacherEmail)
      const existing = teacherNamesByEmail[emailKey]
      if (!existing || (rawTeacherName && existing === nameFromEmailLocal(teacherEmail))) {
        teacherNamesByEmail[emailKey] = name
      }
    }
  }

  for (const entry of noIdStudents.values()) {
    warnings.push({
      row: entry.firstRow,
      message:
        `${entry.name || 'A student'} has no ${studentIdColumn || 'student ID'} — ` +
        `all ${entry.rows} class row(s) skipped, so no schedule can be imported. ` +
        'Add the ID in Aeries and re-export.'
    })
  }

  const aeriesKeyedCount = Object.values(students).filter((s) => s.matchOn === 'aeriesId').length
  if (aeriesKeyedCount) {
    warnings.push({
      row: 0,
      message:
        `${aeriesKeyedCount} student(s) have no SSID. They match on Aeries student ID (STU.ID) ` +
        'until a state ID is in the file. Link each one to the existing app student once.'
    })
  }

  const excludedCount = Object.keys(excludedStudents).length
  if (excludedCount) {
    const codes = [...new Set(Object.values(excludedStudents).map((s) => s.programRaw))]
      .filter(Boolean)
      .join(', ')
    warnings.push({
      row: 0,
      message:
        `${excludedCount} student(s) excluded by program mapping` +
        (codes ? ` (code${codes.includes(',') ? 's' : ''} ${codes} set to Exclude).` : '.')
    })
  }

  return {
    students,
    excludedStudents,
    allTeacherEmails: Array.from(allTeacherEmails),
    teacherNamesByEmail,
    warnings,
    stats: {
      totalRows: rows.length,
      uniqueStudents: Object.keys(students).length,
      excludedStudents: excludedCount,
      skippedRows,
      warningCount: warnings.length
    }
  }
}
